/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 27th 2022, 3:16:19 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Badge from "@mui/material/Badge";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CheckIcon from "@mui/icons-material/Check";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { AutoSaveAPIForm, ImageUploadFormInput } from "ui-widgets/forms";
import { useRegistrationProfileAPIForm } from "users/services/UserAPI";
import { RegistrationProfile, ProfileImagePatchValidationSchema, User } from "lib/schemas";
import { StyledOSDialog, StyledOSDialogProps, OSCircularProgress } from "ui-widgets";

interface UserProfileImageFormProps extends Pick<StyledOSDialogProps, "open" | "onClose"> {
  registrationProfile: RegistrationProfile;
  setUser: React.Dispatch<React.SetStateAction<User>>;
}

export const UserProfileImageForm = ({ registrationProfile, open, onClose, setUser }: UserProfileImageFormProps) => {
  const useRegistrationProfileAPIFormReturn = useRegistrationProfileAPIForm(
    { id: registrationProfile?.id, image: registrationProfile?.image },
    (profileData: RegistrationProfile) => setUser((prev) => ({ ...prev, registrationprofile: profileData })),
    ProfileImagePatchValidationSchema
  );

  const {
    isSendingDataToAPI,
    useFormReturn: {
      formState: { isSubmitting },
    },
  } = useRegistrationProfileAPIFormReturn;

  return registrationProfile?.id ? (
    <StyledOSDialog
      className="os-upload-image-modal"
      popUpIcon={<PhotoCameraIcon color="secondary" />}
      popUpTitle="Upload"
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="sm"
    >
      <AutoSaveAPIForm
        showSubmitButton={false}
        showFeedbackAlerts={false}
        useAPIFormReturn={useRegistrationProfileAPIFormReturn}
      >
        <div style={{ textAlign: "center" }}>
          <ImageUploadFormInput<RegistrationProfile>
            schema_field_key="image"
            renderUploadComponent={({ imagePreviewURL }, { isSuccessfulCrop }) =>
              isSendingDataToAPI || isSubmitting ? (
                <OSCircularProgress style={{ width: 64, height: 64 }} />
              ) : (
                <Stack spacing={2}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    // badgeContent={<PhotoCameraIcon fontSize="large" color="primary" />}
                  >
                    <Avatar
                      sx={{
                        width: 128,
                        height: 128,
                      }}
                      src={imagePreviewURL ?? registrationProfile?.image}
                    />
                  </Badge>

                  <label htmlFor="image">
                    <Button component="span" variant="outlined">
                      Upload Image
                    </Button>
                  </label>

                  {isSuccessfulCrop && !isSendingDataToAPI ? (
                    <Button
                      variant="contained"
                      component="span"
                      size="large"
                      startIcon={<CheckIcon />}
                      onClick={(e) => onClose(e, "backdropClick")}
                    >
                      This looks good!
                    </Button>
                  ) : null}
                </Stack>
              )
            }
          />
        </div>
      </AutoSaveAPIForm>
    </StyledOSDialog>
  ) : null;
};
